import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Heading, Hint, Responsive, Text, ToggleButton, useResponsive } from '@common-fe/common-fe';

import { useIsFullLiquidation } from '../hooks';

import { InvestmentsTab } from './stores/useInvestmentTab.store';
import InvestmentsButtons from './InvestmentsButtons';
import { useInvestmentTabStore } from './stores';

interface Props {
  accountId?: string;
}

// This shadow out of WhiteLabeling
const SHIFTED_SHADOW = '0 8px 25px 0 rgba(0, 38, 102, 0.25)';

const InvestmentsTopbar: React.FC<Props> = ({ accountId }) => {
  const { isFullLiquidated } = useIsFullLiquidation({
    accountId,
  });
  const setCurrentTab = useInvestmentTabStore((state) => state.setCurrentTab);

  useEffect(() => {
    setCurrentTab(InvestmentsTab.Dashboard);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const currentTab = useInvestmentTabStore((state) => state.currentTab);

  return (
    <Box
      direction={isMobile ? 'column' : 'row'}
      background="canvas"
      data-testid="investment"
      align="center"
      justify="between"
      round={{ corner: 'top', size: 'container1Round' }}
      pad={isMobile ? { bottom: 'spacing32', top: 'spacing16', horizontal: 'spacing12' } : undefined}
      margin={isMobile ? undefined : { bottom: 'l' }}
      style={
        isMobile
          ? {
              boxShadow: SHIFTED_SHADOW,
              position: 'fixed',
              bottom: 0,
              right: 0,
              left: 0,
              zIndex: 999,
            }
          : {}
      }
    >
      <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
        <Box direction="row" align="center">
          <Heading level={2} size="regular">
            {currentTab === InvestmentsTab.Dashboard && t('Investments')}
            {currentTab === InvestmentsTab.Performance && t('Performance')}
            {currentTab === InvestmentsTab.Activity && t('Activities')}
          </Heading>
        </Box>
      </Responsive>
      <Box direction={isMobile ? 'column-reverse' : 'row'}>
        <Box
          direction="row"
          background="module"
          pad="xsmall"
          round="container1Round"
          margin={isMobile ? { top: 'spacing16' } : { right: 'spacing16' }}
        >
          <ToggleButton
            testId="dashboard_tab"
            active={currentTab === InvestmentsTab.Dashboard}
            onClick={() => setCurrentTab(InvestmentsTab.Dashboard)}
          >
            <Text weight={currentTab === InvestmentsTab.Dashboard ? 700 : 400}>{t('Dashboard')}</Text>
          </ToggleButton>
          <Hint
            disabled={!isFullLiquidated}
            hideHint={!isFullLiquidated}
            hintWidth="220px"
            hintElement={
              <ToggleButton
                testId="dashboard_performance"
                active={currentTab === InvestmentsTab.Performance}
                disabled={isFullLiquidated}
                onClick={() => !isFullLiquidated && setCurrentTab(InvestmentsTab.Performance)}
              >
                <Text weight={currentTab === InvestmentsTab.Performance ? 700 : 400}>{t('Performance')}</Text>
              </ToggleButton>
            }
          >
            <Text size="small" textAlign="center">
              {t(
                "Performance data isn't available. Start a new investment portfolio to track your progress."
              )}
            </Text>
          </Hint>
          <ToggleButton
            testId="dashboard_activities"
            active={currentTab === InvestmentsTab.Activity}
            onClick={() => setCurrentTab(InvestmentsTab.Activity)}
          >
            <Text weight={currentTab === InvestmentsTab.Activity ? 700 : 400}>{t('Activities')}</Text>
          </ToggleButton>
        </Box>
        <InvestmentsButtons />
      </Box>
    </Box>
  );
};
export default InvestmentsTopbar;
